import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DeckGroup, DeckSlide } from '../../deck-admin.models';

/**
 * Confirmation dialog for soft-deleting a category (with all its slides),
 * a single slide, or a bulk checkbox selection of slides. Exactly one of
 * `group` / `slide` / `bulkCount > 0` is expected at a time.
 */
@Component({
  selector: 'app-deck-delete-confirm-modal',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div
      class="fixed inset-0 z-[9000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      id="deck_delete_confirm_modal"
    >
      <div class="bg-white dark:bg-surface-1 rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4" (click)="$event.stopPropagation()">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <svg class="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
          <div>
            <h3 class="text-sm font-bold text-slate-800 dark:text-white">
              {{ group ? 'Delete Category?' : 'Delete Slide(s)?' }}
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">This is a soft delete.</p>
          </div>
        </div>

        <p *ngIf="group" class="text-xs text-slate-600 dark:text-slate-300 mb-5">
          Delete <strong>{{ group!.title }}</strong> and its
          <strong>{{ group!.pageCount ?? 0 }}</strong> slide(s)? They go together —
          a slide with no category is reachable by nothing.
        </p>
        <p *ngIf="slide" class="text-xs text-slate-600 dark:text-slate-300 mb-5">
          Delete <strong>{{ slide!.title }}</strong>
          (<code class="font-mono bg-slate-100 dark:bg-slate-700 px-1 rounded text-purple-600 dark:text-purple-400 text-[10px]">{{ slide!.key }}</code>)?
        </p>
        <p *ngIf="bulkCount > 0" class="text-xs text-slate-600 dark:text-slate-300 mb-5">
          Delete <strong>{{ bulkCount }}</strong> selected slide(s)?
        </p>

        <div class="flex gap-3">
          <button (click)="cancel.emit()"
                  class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                  id="deck_delete_cancel_btn">Cancel</button>
          <button (click)="confirm.emit()" [disabled]="deleting"
                  class="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-60 transition-colors"
                  id="deck_delete_confirm_btn">
            <span *ngIf="!deleting">Delete</span>
            <span *ngIf="deleting" class="flex items-center justify-center gap-2">
              <span class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              Deleting…
            </span>
          </button>
        </div>
      </div>
    </div>
  `,
})
export class DeckDeleteConfirmModalComponent {
  /** Category to delete (mutually exclusive with `slide`). */
  @Input() group: DeckGroup | null = null;
  /** Slide to delete (mutually exclusive with `group`). */
  @Input() slide: DeckSlide | null = null;
  /** Number of checkbox-selected slides for a bulk delete; 0 = not a bulk delete. */
  @Input() bulkCount = 0;
  /** True while the delete request is in flight. */
  @Input() deleting = false;

  @Output() cancel = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<void>();
}
